import { Dictionary, Utilities } from '../../helper/util';
import { DateTimeHelper } from '../../helper/datetime.helper';
import { IUploadConfig } from '../content-upload/content-upload.component';
import { AlertController, NavController, ToastController } from '@ionic/angular';
import { Component, Output, Input, EventEmitter, OnChanges, SimpleChanges, ViewChild, ViewChildren, QueryList, ElementRef, ContentChild, TemplateRef } from '@angular/core';
import { BrowserHelper } from '../../helper/browser.helper';
import { Upload } from '../../data/schema/upload';
import { Log } from '../../log';

/**
 * Generated class for the DataTableComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */

export enum AddItemType {
  NONE = 0, //won't display any add option; this is default
  INLINE = 1, //allows inline addition; ie. the datatable will handle the addition of a new item
  BUTTON = 2, //use if you want to handle the addition of an item separately
}

export enum MediaOptions {
  PDF = '.pdf',
  CSV = '.csv',
  PNG = '.png',
  JPG = '.jpg',
  JPEG = '.jpeg'
}

export enum InputOptions {
  TEL = 'tel',
  URL = 'url',
  DATE = 'date',
  TIME = 'time',
  TEXT = 'text',
  EMAIL = 'email',
  NUMBER = 'number',
  SELECT = 'select',
  CONTENT = 'content',
  CURRENCY = 'currency',
  PERCENT = 'percent',
  CHECKBOX = 'checkbox',
  BUTTON = 'button',
  BUTTONARRAY = 'button-array',
  TEXTARRAY = 'text-array',
  BOOLEAN = 'boolean',
}

@Component({
  selector: 'data-table',
  templateUrl: 'data-table.html',
  styleUrls: ['data-table.scss']
})
export class DataTableComponent implements OnChanges {

  @Output() itemSelected: EventEmitter<any> = new EventEmitter();
  @Output() multiSelectAction: EventEmitter<any> = new EventEmitter();
  @Output() addItemClicked: EventEmitter<any> = new EventEmitter();
  @Output() itemAdded: EventEmitter<any> = new EventEmitter();
  @Output() inputBlur: EventEmitter<any> = new EventEmitter(); // used for text input edits
  @Output() itemsSorted: EventEmitter<void> = new EventEmitter();
  @Output() reorderToggled: EventEmitter<boolean> = new EventEmitter();
  @Output() rowButtonClicked: EventEmitter<any> = new EventEmitter();
  @Output() itemChanged: EventEmitter<IItemChange> = new EventEmitter();
  @Output() itemArchived: EventEmitter<any> = new EventEmitter();
  @Output() mediaUploaded: EventEmitter<any> = new EventEmitter();
  @Output() mediaCleared: EventEmitter<any> = new EventEmitter();
  @Output() clipboardCopied: EventEmitter<any> = new EventEmitter();
  @Output() didSearch: EventEmitter<string> = new EventEmitter();
  @Output() didFilter: EventEmitter<string> = new EventEmitter();

  @Input() tableOptions: DataTableOptions = new DataTableOptions();
  @Input() busy = false;
  @Input() tableWidth = '100%';

  @ViewChildren('dateInput') date?: QueryList<ElementRef>;
  @ViewChild('newDateInput') newDate?: ElementRef;

  @ContentChild(TemplateRef) customFilterTemplate: TemplateRef<any>;
  @ContentChild(TemplateRef) customLastColTemplate: TemplateRef<any>;

  sortBy?: string;
  sortAsc = false;
  reorder = false;
  newItem: any = {}; searchFilter?: string;
  filteredDataSet: any[] = [];
  clearedImages: number[];
  todayDateFormat = DateTimeHelper.formatDateShort();

  // this is a dictionary of primary key to object
  selectedItems: Dictionary<any> = {};
  // dictionary for items in edit mode
  editItemDic: Dictionary<any> = {};

  totalRows: number[] = [];

  // dictionary mapping available filters to their selected value
  filtersSelected: Dictionary<string> = {};
  currFilter: string;
  selectedAll = false;

  // Track form validity for the add item row
  isAddFormValid = false;

  private sortByHeader?: ITableHeader;
  private newImageIndex = -8;  //-8 is for adding a new items, not sure why -8 but yea

  constructor(
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private browserHelper: BrowserHelper,
    protected navCtrl: NavController
  ) {
    this.clearedImages = [this.newImageIndex];
  }

  ngOnChanges(changes: SimpleChanges) {
    // Log.debug('datatable changes', changes);
    if (this.tableOptions != null) {
      //Log.debug('  - options not null, filter');
      if (this.tableOptions.initialSearchTerm != null && Utilities.stringEmpty(this.searchFilter)) {
        this.searchFilter = this.tableOptions.initialSearchTerm;
      }
      if (this.tableOptions.filters != null) {
        this.tableOptions.filters.forEach(f => {
          this.filtersSelected[f.field] = f.default;
        });
      }
      this.filterDataset();
      // Initialize form validity when table options change
      this.updateAddFormValidity();
    } else if (this.tableOptions == null) {
      //Log.debug('  - no options, initialize with blank');
      this.tableOptions = new DataTableOptions();
    }
    if (this.searchFilter != null) {
      //Log.debug('  - search filter not null, re-apply');
      this.search();
    }
    if (this.tableOptions.autoSort) {
      //Log.debug('  - run auto-sort');
      this.autosort();
    }
  }

  refresh() {
    this.filterDataset();
  }

  pickDate(index: number) {
    const inputArray = this.date?.toArray() || [];
    // Log.debug('date inputs', inputArray)
    if (inputArray.length > 1) {
      inputArray[index].nativeElement.showPicker();
    } else {
      inputArray[0].nativeElement.showPicker();
    }
  }

  toggleSelectAll() {
    if (this.selectedAll) {
      this.filteredDataSet.forEach(item => {
        this.selectedItems[item[this.tableOptions.primaryKey ?? '']] = true;
      });
    } else if (this.hasSelectedAll()) {
      this.clearSelectedItems();
    }
  }

  toggleReorder() {;
   this.reorderToggled.emit(this.reorder);
  }

  hasSelectedAll() {
    return this.getSelectedItems().length === this.filteredDataSet.length;
  }

  toggleItemSelected(item: any) {
    const pk = item[this.tableOptions.primaryKey ?? ''];
    const selectedItem = this.selectedItems[pk];
    if (selectedItem) {
      this.selectedItems[pk] = false;
    } else {
      this.selectedItems[pk] = true;
    }

    this.selectedAll = this.hasSelectedAll();
  }

  emitMultiSelectAction(action: string) {
    const selectedItems = this.getSelectedItems();
    this.multiSelectAction.emit({ action, items: selectedItems });
  }

  getSelectedItems() {
    const selectedItems = [];
    for (const [key, value] of Object.entries(this.selectedItems)) {
      if (value === true) {
        selectedItems.push(+key);
      }
    }
    return selectedItems;
  }

  hasSelectedItems() {
    return Object.values(this.selectedItems).some(value => value === true);
  }

  clearSelectedItems() {
    this.selectedItems = {};
    this.selectedAll = false;
  }

  onBlur(pk: number, evt: any) {
    const val = evt.target.value;
    this.inputBlur.emit(new DataTableBlurResp({
      primaryKey: pk,
      value: val
    }));
  }

  applySort(headField: string, asc: boolean) {
    // WAS CALLING THIS FROM VARIOUS PAGES, NOW HANDLING WITH LOCALSTORAGE SAVING SORT ORDER ME THINKS
    //this.sort(this.sortByHeader, false);
    const head = this.tableOptions.headers.find(c => c.field === headField);
    // set same field as prev sort so our sort order is respected
    this.sortBy = headField;
    this.sortAsc = !asc; // sort func flips it like it was clicked again, so negate it
    this.sort(head);
  }

  uploadMedia(event: any) {
    const media: any[] = [];
    if (!event || event.target.files.length === 0) { return; }
    Log.debug('upload event emitter', event.target.files);
    const myReader: FileReader = new FileReader();
    // Attempt to read the base64 for the asset
    myReader.readAsDataURL(event.target.files[0]);
    myReader.onloadend = (async _ => {
      media.push(myReader.result?.toString());
      this.mediaUploaded.emit(media);
    });
  }

  sort(column: ITableHeader) {
    if (column == null || column.sortable !== true) { return; }

    // check if same column clicked, if so, reverse sort order, else default to ascending
    if (this.sortBy === column.field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortAsc = true;
    }

    if (this.tableOptions.autoSort) {
      // store selected sort on specific page for specific table so we can auto-sort on refresh
      const key = window.location.href.substring(window.location.href.lastIndexOf('/') + 1) + this.tableOptions.type;
      localStorage.setItem(key, JSON.stringify(column));
      localStorage.setItem(key + '-order', this.sortAsc ? '1' : '0');
    }

    this.doSort(column, this.sortAsc);
  }

  autosort() {
    if (!this.tableOptions.autoSort) {
      return;
    }

    const key = window.location.href.substring(window.location.href.lastIndexOf('/') + 1) + this.tableOptions.type;
    const col = localStorage.getItem(key);
    const asc = localStorage.getItem(key + '-order');
    if (col == null) { return; }
    this.doSort(JSON.parse(col) as ITableHeader, asc === '1' ? true : false);
  }

  doSort(column: ITableHeader, sortAscending: boolean) {
    Log.debug('sorting in ascending ' + sortAscending, column);
    this.sortByHeader = column;
    this.sortBy = column.field;
    this.tableOptions.dataSet.sort((a, b) => this.sortFunc(a, b, sortAscending, column.isDate, column.isNumber));
    this.filteredDataSet.sort((a, b) => this.sortFunc(a, b, sortAscending, column.isDate, column.isNumber));
  }

  async copyToClipboard(value: any, colName: any) {
    await navigator.clipboard.writeText(value.toString());
    this.clipboardCopied.emit(colName);
    const toast = await this.toastCtrl.create({
      duration: 3000,
      position: 'top',
      message: `${colName} copied to clipboard.`
    });
    await toast.present();
  }

  reorderItems(event: any) {
    const indexes = event.detail;
    // figure out positions
    const from = indexes.from;
    const to = indexes.to;

    // Update position
    const element = this.tableOptions.dataSet[from];
    this.tableOptions.dataSet.splice(from, 1);
    this.tableOptions.dataSet.splice(to, 0, element);

    event.detail.complete();

    // Save them
    this.itemsSorted.emit();

    //this.refresh();
  }

  selectByPk(item: any) {
    this.itemSelected.emit(item);
  }

  rowButtonClick(event: Event, item: any) {
    event.stopPropagation();
    this.rowButtonClicked.emit(item);
  }

  columnClick(item: any) {
    this.tableOptions.clickableColumnCallback(item);
  }

  columnClick2(item: any) {
    this.tableOptions.clickableColumnCallback(item);
  }

  // this just avoids needing to keep reference to passed in filter, since in most cases there's
  // likely only 1, or if multiple, they have unique values
  applyFilterSimple(val: string) {
    for (const f of this.tableOptions.filters) {
      for (const o of f.values) {
        if (o === val) {
          this.filtersSelected[f.field] = val;
          this.filterDataset(f);
          return;
        }
      }
    }
  }

  applyFilter(filter: ITableFilter, val: string) {
    this.filtersSelected[filter.field] = val;
    this.filterDataset(filter);
  }

  filterDataset(filter?: ITableFilter) {
    // start with full set
    this.filteredDataSet = this.tableOptions.dataSet;

    // run through external filter if provided
    if (filter?.changeCallback != null) {
      filter.changeCallback(this.filtersSelected[filter.field]);
    }

    if (filter?.matchFunc != null) {

    }

    // new way to apply filters is to iterate through the filters and continue to whittle down the filtered set
    if (this.tableOptions.filters != null) {
      this.tableOptions.filters.forEach(tableFilter => {
        if (this.filtersSelected[tableFilter.field]) {

          if (tableFilter.matchFunc != null) {
            // in this case we are actually using a function on the data set item object to
            // determine if it matches the filter or not
            this.filteredDataSet = this.filteredDataSet.filter(
              dataSetItem => dataSetItem[tableFilter.matchFunc](this.filtersSelected[tableFilter.field].toLowerCase())
            );
          } else {
            this.filteredDataSet = this.filteredDataSet.filter(dataSetItem => {
              if (tableFilter.partialMatch) {
                return (dataSetItem[tableFilter.field]?.toLowerCase().includes(this.filtersSelected[tableFilter.field].toLowerCase()));
              } else {
                return (dataSetItem[tableFilter.field] === this.filtersSelected[tableFilter.field]);
              }
            });
          }
        }
      });
    }

    // then if we have any search string entered, filter by that as well
    if (!Utilities.stringEmpty(this.searchFilter)) {
      Log.debug('apply search filter');
      this.search();
    }

    if (this.tableOptions.enableMultiSelect && this.tableOptions.primaryKey != null) {
      // if checkboxes enabled, also require the primary key on data objects, then init checkbox to unselected
      this.filteredDataSet.forEach(m => {
        this.selectedItems[m[this.tableOptions.primaryKey ?? '']] = false;
      });
    }

    if (filter != null) {
      this.currFilter = this.filtersSelected[filter.field];
      this.didFilter.emit(this.filtersSelected[filter.field]);
    }

    if (this.tableOptions.showSearch) {
      this.didSearch.emit(this.searchFilter ?? '');
    }
  }

  openUrl(url: string) {
    this.browserHelper.openUrl(url);
  }


  runCustomClick(event: any) {
    event();
  }

  updateDateFormat(event: any, name: string) {
    this.todayDateFormat = DateTimeHelper.formatDateShort(event.target.value);
    // Update form validity when date changes
    this.onInputChange();
  }

  addBtnClicked() {
    this.addItemClicked.emit();
  }

  addItem() {
    // makes checks for required before emiting new item
    let missing = '';
    this.tableOptions.columns.forEach((col, i) => {
      if (!col.isRequired) {
        // do nothing
      } else if (col.type === InputOptions.CONTENT && this.clearedImages.includes(this.newImageIndex)) {
        missing += `• ${this.tableOptions.headers[i].label} is required<br/>`;
      } else if ((col.type === InputOptions.NUMBER || col.type === InputOptions.CURRENCY) && isNaN(this.newItem[col.classProperty])) {
        missing += `• ${this.tableOptions.headers[i].label} is required<br/>`;
      } else if (col.type !== InputOptions.CONTENT &&
        col.type !== InputOptions.CHECKBOX &&
        col.type !== InputOptions.NUMBER &&
        col.type !== InputOptions.CURRENCY &&
        Utilities.stringEmpty(this.newItem[col.classProperty])) {
        missing += `• ${this.tableOptions.headers[i].label} is required<br/>`;
      }
    });

    if (missing.length > 0) {
      this.displayAlert('Missing Fields', missing);
    } else {
      this.itemAdded.emit({ ...this.newItem });
    }
  }

  resetAddItem() {
    this.tableOptions.columns.forEach((col) => {
      if (col.type === InputOptions.CONTENT) {
        this.newItem[col.classProperty] = [];
      } else if (col.type === InputOptions.CHECKBOX) {
        this.newItem[col.classProperty] = false;
      } else if (this.newItem[col.classProperty] !== 'function') {
        this.newItem[col.classProperty] = null;
      }
    });
    Log.debug('After resetting', this.newItem);
    // Reset form validity when clearing the form
    this.isAddFormValid = false;
  }

  // Handle validation events from input components
  onInputValidationChange(component: any, isValid: boolean) {
    // Update the component's validity state
    component.isValid = isValid;
    // Re-evaluate overall form validity
    this.updateAddFormValidity();
  }

  // Update the overall form validity based on all input components

  // Handle input change events to trigger validation
  onInputChange() {
    // Trigger validation check after a short delay to allow the input to update
    setTimeout(() => {
      this.updateAddFormValidity();
    }, 100);
  }

  searchHelp() {
    let message = 'Search by:<br/>';
    this.tableOptions.searchFields.forEach(field => {
      // look up the label for the field
      let label = this.tableOptions.headers.find(h => h.field === field.field)?.label;
      if (label != null && label.endsWith('*')) {
        label = label.slice(0, -1);
      }
      if (Utilities.stringEmpty(label)) {
        label = field.field;
      }

      message += `• ${label} ${field.exactMatch ? '(Exact Match)' : ''}<br/>`;
    });
    this.displayAlert('Search Help', message);
  }

  editItem(index: number) {
    // it is possible to have filtered items, so we need to find the index in the dataset
    // for the index we passed in.
    let oldIndex = index;
    if (this.tableOptions.primaryKey != null) {
      const item = this.filteredDataSet[index];
      oldIndex = this.tableOptions.dataSet.findIndex(i => i[this.tableOptions.primaryKey] === item[this.tableOptions.primaryKey]);
    }

    if (this.inputsValid(index)) {
      this.itemChanged.emit({
        index: oldIndex,
        newValue: this.editItemDic[index],
        oldValue: this.tableOptions.dataSet[oldIndex]
      });
      delete this.editItemDic[index];
    }
  }

  toggleEdit(index: number) {
    // it is possible to have filtered items, so we need to find the index in the dataset
    // for the index we passed in.
    let itemIndex = index;
    if (this.tableOptions.primaryKey != null) {
      const item = this.filteredDataSet[index];
      itemIndex = this.tableOptions.dataSet.findIndex(i => i[this.tableOptions.primaryKey] === item[this.tableOptions.primaryKey]);
    }

    // remove copied item for dictionary if they cancel edit
    if (this.editItemDic[index]) {
      delete this.editItemDic[index];
    } else {
      this.editItemDic[index] = { ...this.tableOptions.dataSet[itemIndex] };
    }
    Log.debug('Items to edit', this.editItemDic);
  }

  archiveItem(index: number) {
    // the index is of the filtered dataset, so we need to find the index of the item in the dataSet
    const item = this.filteredDataSet[index];
    const itemIndex = this.tableOptions.dataSet.findIndex(i => i[this.tableOptions.primaryKey] === item[this.tableOptions.primaryKey]);

    Log.debug('Archived Index', index, itemIndex, item);
    this.itemArchived.emit(itemIndex);
  }

  selectedFile(file: Upload, index?: number) {
    if (file) {
      Log.debug('Image selected', file, index);
      const imgIndex = this.clearedImages.findIndex(i => i === (index != null ? index : this.newImageIndex));
      this.clearedImages.splice(imgIndex, 1);
      Log.debug('cleared images', imgIndex, this.clearedImages);
      this.mediaUploaded.emit({ file, index });
      // Update form validity when content is uploaded
      this.updateAddFormValidity();
    }
  }

  clearImage(index?: number) {
    const indexToAdd = index != null ? index : this.newImageIndex;
    this.clearedImages.push(indexToAdd);
    this.mediaCleared.emit({ index });
    Log.debug('cleared images', this.clearedImages);
    // Update form validity when content is cleared
    this.updateAddFormValidity();
  }

  getColumnTotal(column: any) {
    if (this.tableOptions.totalColumns.includes(column)) {
      return this.tableOptions.dataSet
        .reduce((prevVal, currVal) => prevVal + +currVal[column], 0);
    }
    return '';
  }

  getOptionDisplayValue(colName: string, value: any) {
    const column = this.tableOptions.columns.find(c => c.classProperty === colName);
    if (column) {
      return column.options.find(op => op.value === value)?.displayValue ?? value;
    }
    return value ?? '';
  }

  displayHelp(text: string) {
    this.displayAlert('', text);
  }


  private updateAddFormValidity() {
    // Check if there are any invalid elements in the form
    const thisTable = document.getElementById(this.tableOptions.id);
    const invalidElements = thisTable?.getElementsByClassName('invalid-el') ?? [];
    const hasInvalidElements = invalidElements?.length > 0;

    // Check content upload requirements
    const contentColumn = this.tableOptions.columns.find(col => col.type === InputOptions.CONTENT);
    const hasContentIssue = contentColumn && contentColumn.isRequired && this.clearedImages.includes(this.newImageIndex);

    // Check required fields
    const hasMissingRequired = this.tableOptions.columns.some((col, i) => {
      if (!col.isRequired) {
        return false;
      }

      const value = this.newItem[col.classProperty];

      switch (col.type) {
        case InputOptions.CONTENT:
          return this.clearedImages.includes(this.newImageIndex);
        case InputOptions.CHECKBOX:
          return value === null || value === undefined;
        case InputOptions.NUMBER:
        case InputOptions.CURRENCY:
          return value === null || value === undefined || isNaN(value);
        default:
          return Utilities.stringEmpty(value);
      }
    });

    this.isAddFormValid = !hasInvalidElements && !hasContentIssue && !hasMissingRequired;
  }

  private sortFunc(a, b, sortAscending, isDate: boolean = false, isNumber: boolean = false) {
    // Grab the fields we wish to sort by
    let valA = a[this.sortBy];
    let valB = b[this.sortBy];
    // Lowercase strings so it's a case insensitive sort
    if (isNumber || (this.sortBy === 'landingNumbers' && valA.match(/\d+/g) && valB.match(/\d+/g))) {
      if (typeof valA === 'string' && typeof valB === 'string') { // if number is in string format
        // replace non alphanumeric chars
        valA.replace(/[\W_]+/g, ''); // https://stackoverflow.com/a/20864946/7015266
        valB.replace(/[\W_]+/g, '');
        return sortAscending ? parseInt(valB, 10) - parseInt(valA, 10) : parseInt(valA, 10) - parseInt(valB, 10);
      }
      return sortAscending ? valB - valA : valA - valB;
    } else if (isDate) {
      valA = new Date(valA);
      valB = new Date(valB);

      if (isNaN(valA)) { valA = null; }
      if (isNaN(valB)) { valB = null; }
    } else {
      if (typeof valA === 'string') {
        valA = valA.toLowerCase();
      }
      if (typeof valB === 'string') {
        valB = valB.toLowerCase();
      }
    }
    // Run the sort logic
    if (valA < valB || valA == null) {
      return (sortAscending ? -1 : 1);
    } else if (valA > valB || valB == null) {
      return (sortAscending ? 1 : -1);
    } else {
      if (valA == null && valB == null) {
        return 0;
      }
      //return 0;
      // if equal, sort by primary key to ensure consistent ordering
      return a[this.tableOptions.primaryKey] - b[this.tableOptions.primaryKey];
    }
  }

  private inputsValid(index?: number) {
    const content = this.tableOptions.columns.find(col => col.type === InputOptions.CONTENT);

    const thisTable = document.getElementById(this.tableOptions.id);
    const invalidElements = thisTable?.getElementsByClassName('invalid-el') ?? [];
    const isFormValid = invalidElements.length === 0;

    if (!isFormValid) {
      this.displayAlert('Form Errors', 'Please correct the form errors first.');
    } else if (index != null && content && content.isRequired && this.clearedImages.includes(index)) {
      const colIndex = this.tableOptions.columns.findIndex(col => col.type === InputOptions.CONTENT);
      this.displayAlert('Missing Information', `Upload a ${this.tableOptions.headers[colIndex].label}`);
      return false;
    }
    return isFormValid;
  }

  private async displayAlert(title: string, message: string) {
    const alert = await this.alertCtrl.create({
      header: title,
      message,
      buttons: ['OK']
    });
    alert.present();
    return alert;
  }


  // should only be called from filter function
  private search() {
    const filter = this.searchFilter?.toLowerCase();
    this.filteredDataSet = this.filteredDataSet.filter(m => {
      for (const searchField of this.tableOptions.searchFields) {
        let val = m[searchField.field];
        if (val != null) {
          if (typeof val === 'number') {
            val = val + '';
          }
          if (!searchField.exactMatch && val.toLowerCase().includes(filter)) {
            return true;
          } else if (searchField.exactMatch && val.toLowerCase() === filter) {
            return true;
          }
        }
      };
      return false;
    });
  }

}

export class DataTableBlurResp {
  primaryKey?: number;
  value?: string;
  constructor(init?: DataTableBlurResp) {
    Object.assign(this, init);
  }
}

export class DataTableOptions {
  id?: string;
  addBtnLabel?: string;
  addType?: AddItemType = AddItemType.NONE;
  allowDelete?: boolean = false;
  allowEdit?: boolean = false;
  autoSort?: boolean = true;
  bgProp?: string;
  clickableColumn?: number = -1; //index of column you want to be clickable
  clickableColumnCallback?: any; //index of column you want to be clickable
  columnClickRoute?: string = ''; //use if you want to direct the user to a different page when an item is clicked
  columns: ITableColumn[] = [];
  contentUploadConfig?: IUploadConfig; //used when the input type is CONTENT
  customActions?: ICustomButton[] = []; //use if you want add custom actions other than the current functionality
  customButtons?: ICustomButton[] = []; //use if you want add custom buttons other than the current functionality
  dataSet: any[] = [];
  enableClickableColumn?: boolean = false; //if specified, a clickable column with an index will be
  enableClickableColumnForCheckCol?: boolean = false;
  enableMultiSelect?: boolean = false;
  filters?: ITableFilter[] = []; //use if you want to allow the user to filter the dataset
  headers: ITableHeader[] = [];
  includeLastColTemplate?: boolean = false;
  includePagination?: boolean = false; //if specified will paginate the results shown in the table
  includeRowButton?: boolean = false;
  indicateNewItems?: boolean = false;
  mediaOptions?: MediaOptions[] | string; //this is currently used to specify file types for the import button
  newItemLookUp?: string = 'isNew';
  noItemsMessage?: string = 'No items found'; //message to show if dataset is empty
  paginationItemsPerPage?: number = 15; //number of items to show at a time
  paginationCurrentPage?: number = 1; //index of list to be displayed first
  paginationTotalItems?: number; //total number of items in the list
  primaryKey?: string;
  propertyForDisabled?: string;
  rowButtonHeader?: string = '';
  rowButtonText?: string = 'Select';
  searchPlaceholder?: string = 'Enter Search Criteria';
  searchFields?: ISearchField[] = []; //fields in object that can be searched
  initialSearchTerm?: string;
  showImportButton?: boolean = false;
  showReorder?: boolean = false;
  showSearch?: boolean = false;
  showTableHeader?: boolean = false;
  showTotalRow?: boolean = false; //determines wether to show column totals for the table contents
  tableCssClass?: string; //if specified will add custom css to whole table
  tableHeader?: string = 'Table Header';
  totalColumns?: any[] = []; //determines which columns to sum up
  type?: string;

  constructor(init?: DataTableOptions) {
    Object.assign(this, init);
    if (this.mediaOptions) { this.mediaOptions = this.mediaOptions.toString(); }
    if (this.id == null) {
      this.id = 'data-table-' + Utilities.getRandomString(10);
    }
  }
}

export interface ISearchField {
  field: string;
  exactMatch?: boolean;
}

export interface ITableHeader {
  label?: string; //the column title displayed to the user
  field?: string; //the field on the object to be displayed (used for the filtering)
  //sortField?: string; // optional, if provided overrides field when sorting
  sortable?: boolean; //determines if column will be considered whiles sorting the table contents
  isLocked?: boolean; //shows lock icon and lock message
  isNumber?: boolean; // causes right alignment
  isDate?: boolean;
  minWidth?: string; //specifies the min width of the column eg '100px'
  helpText?: string; //displays a help icon that shows this text when clicked
}

export class ITableColumn {
  classProperty: string; //field on the class
  step?: string; //used for numper inputs
  canEdit?: boolean; //used to determine if the field can be when adding inline
  currency?: string; //specify currency when your input type is currency
  currencyCol?: string; //specify currency column when your input type is currency
  currencyFormat?: string = '1.0-0'; //specify currency format when your input type is currency
  maxLength?: string | number | null; //configures the max lenght of characters
  type?: InputOptions = InputOptions.TEXT; //determines what input component will be used
  isRequired?: string | boolean; //used to check when inline editing or adding a new object
  copyOption?: boolean; //shows a copy icon next to field content that allows the user to copy the content
  copyIconOnly?: boolean;
  displayValue?: string; //used for the date input, specifying this option uses our custom date input with the specify format (pass in a field with the desired format)
  enableInlineEdit?: boolean; //determines if a field can be editable when the users want to edit a row
  minWidth?: string; //specifies the min width of the column eg '100px'
  maxWidth?: string; //specifies the max width of the column eg '100px'
  options?: { value: any; displayValue: string }[] = []; //used for when input type is SELECT
  customColor?: string; //adds specified color to column
  shrinkCell?: boolean; //determines if the column can shrink to fit-content
  url?: string; // to provide optional url to open on click, not displayed
  callback?: any; // options callback for buttons
  buttons?: ICustomButton[] = [];
  linkCallback?: any; // makes a column a link that executes callback on click
  nowrap?: boolean = true;
  arrayProperty?: string; // used for text-array input type to indicate which field to show from the array
  dateFormat?: string;
  showWarningIcon?: boolean = false; // used to show warning icon beside the cell content 
  showInSort?: boolean = true; // used to show the column in the sort dropdown

  constructor(init?: Partial<ITableColumn>) {
    Object.assign(this, init);
  }
}

export interface IItemChange {
  index: number;
  oldValue: any;
  newValue: any;
}

export interface ICustomButton {
  label?: string;
  id?: string;
  icon?: string;
  color?: string;
  showHelpIcon?: boolean;
  onHelpClick?: () => void;
  onClick: (value?: any) => void;
  cssClass?: string;
  disabled?: boolean; // used in loading page
}

export interface ITableFilter {
  values: string[];
  labels?: string[]; // if not provided, will use values as labels
  default?: string; // initially selected value
  unselectedLabel?: string; // only needed if you can have no filter selected
  field: string;
  partialMatch?: boolean; // if true, will match any value that contains the filter value
  changeCallback?: (value?: any) => void; // like a callback to apply additional filtering
  matchFunc?: string; // function name on objects to call to check if the object matches filter, should take a string
}
